import stringify from "qs-stringify"
import React, { HTMLAttributes, useEffect } from "react"
import ThemeContext from "../context/ThemeContext"
import styled from "styled-components"
import tw from "twin.macro"
import Caption from "./Caption"
import RichText from "./RichText"

export interface MediaBoxProps {
  /**
   * Whether the image should cover the entire container or not. If `true` the
   * `object-fit` property will be set to `cover` and the width and height will
   * be set to `100%`.
   */
  cover?: boolean
  /**
   * The aspect ratio of the image. This is used to set the `padding-bottom`
   * property to maintain the aspect ratio of the image and avoid layout shift.
   * If `cover` is set to `true` this property will be ignored.
   */
  cropRatio?: number
  /**
   * The height of the original image. This is used if `cropRatio` is not set
   * to calculate the aspect ratio of the image.
   */
  imageHeight: number
  /**
   * The width of the original image. This is used if `cropRatio` is not set
   * to calculate the aspect ratio of the image.
   */
  imageWidth: number
}

const MediaBox = styled.div<MediaBoxProps>`
  ${tw`relative overflow-hidden`}
  ${(props) => {
    if (!props.cover) {
      return `
        width: 100%;
        height: 0;
        padding-bottom: ${
          props.cropRatio
            ? `${props.cropRatio * 100}%`
            : `${(props.imageHeight / props.imageWidth) * 100}%`
        };
      `
    }
    if (props.cover) {
      return `
        width: 100%;
        height: 100%;
        object-fit: cover;
        font-family: "blur-up: auto";
      `
    }
  }}
  .image-optim__image ,
  .ls-blur-up-img {
    ${tw`block absolute inset-0 w-full h-full object-cover`}
    font-family: "blur-up: auto";
  }
  .image-optim__image.ls-blur-up-is-loading,
  .image-optim__image.lazyload:not([src]) {
    visibility: hidden;
  }
  .ls-blur-up-img {
    z-index: 1;
    filter: blur(10px);
    opacity: 1;
    transition:
      opacity 300ms,
      filter 450ms;
  }
  .ls-blur-up-img.ls-inview.ls-original-loaded {
    opacity: 0;
    filter: blur(5px);
  }
`

export interface ImageOptimProps extends HTMLAttributes<HTMLImageElement> {
  /**
   * The alt text for the image. This is required for accessibility. Alt text
   * may only be omitted if the image is purely decorative. In that case, you
   * should add an empty alt attribute (alt="") so that assistive technologies
   * know that the image is purely decorative and does not contain any
   * meaningful content.
   */
  alt: string
  /**
   * The caption for the image. This will be displayed below the image.
   */
  caption?: string
  /**
   * The linked items for the caption. This is used to provided data needed
   * to render modular content added in Kontent
   */
  captionLinkedItems?: any[]
  /**
   * The link data for items linked in Kontent, used to resolve links.
   */
  captionLinks?: any[]
  /**
   * Optional classes for the image. This is used to apply custom styles to the
   * image.
   */
  className?: string
  /**
   * Whether the image should cover the entire container or not.
   */
  cover?: boolean
  /**
   * The desired aspect ratio of the image represented as a decimal
   * (e.g. 16:9 = 9/16 = 0.5625). This is used to crop and resize the image. If
   * no cropRatio is set, the image will be displayed at its original aspect
   * ratio.
   */
  cropRatio?: number
  /**
   * The desired width of the image. This is used to optimize the image so the
   * image is not larger than it needs to be.
   */
  cropWidth?: number
  /**
   * The focal point of the image. This is used to ensure the most important
   * part of the image is not cropped out.
   */
  focalPoint?:
    | "left-top"
    | "right-top"
    | "top"
    | "left-bottom"
    | "right-bottom"
    | "bottom"
    | "left"
    | "right"
    | "center"
  /**
   * The height of the original image. This is used if `cropRatio` is not set
   * to calculate the aspect ratio of the image.
   */
  height: number
  /**
   * Whether the image should be lazy loaded or not.
   */
  lazy?: boolean
  /**
   * The region the image is being displayed in. This is used to determine whether
   * captions should have a border or not.
   */
  region?: "aside" | "default"
  /**
   * Whether the caption should be omitted regardless of whether it is set or not.
   * This is used to prevent captions from being displayed in layouts where they
   * don’t make sense, like backgrounds.
   */
  suppressCaption?: boolean
  /**
   *  The URL of the image. This is required.
   */
  url: string
  /**
   * The width of the original image. This is used if `cropRatio` is not set
   * to calculate the aspect ratio of the image.
   */
  width: number
  /**
   * Passes the current theme value to the rich text component used to display
   * captions
   */
  theme?: any
  mediaBoxProps?: HTMLAttributes<HTMLDivElement>
}

/**
 * Used to display images from Kontent. This component will optimize the image
 * for various screen sizes and resolutions using Kontent's Image Transformation
 * API. It will also lazy load the image to improve performance.
 */
export const Picture = ({
  alt = ``,
  caption,
  captionLinkedItems,
  captionLinks,
  className,
  cover,
  cropRatio,
  cropWidth = 750,
  focalPoint,
  height,
  lazy = true,
  region,
  suppressCaption = false,
  url,
  width,
  theme,
  mediaBoxProps,
  ...otherProps
}: ImageOptimProps) => {
  /**
   * Determines aspect ratio image should be cropped to based on `cropRatio`
   * prop and compares it to the aspect ratio of the original image.
   * `cropWidthCalc` and `cropHeightCalc` are then set to the largest possible
   * dimensions based on the width and height of the source image. Those values
   * are used draw the crop rectangle using the Kontent Image Transformation API
   * based on the set focal point.
   *
   * See link for diagram:
   * https://user-images.githubusercontent.com/199782/211621890-15dc7b74-7f25-41c8-af10-a1fef325c1ef.jpg
   */
  let newAspectRatio = 1 / cropRatio
  let oldAspectRatio = width / height
  let cropWidthCalc = width
  let cropHeightCalc = height
  if (oldAspectRatio > newAspectRatio) {
    cropWidthCalc = Math.round(height * newAspectRatio)
  } else if (oldAspectRatio < newAspectRatio) {
    cropHeightCalc = Math.round(width / newAspectRatio)
  }

  /**
   * Gets coordinates for the crop rectangle based on the set focal point.
   *
   * @returns {string} - string of coordinates for the crop rectangle based on
   * the set focal point.
   */
  const rectFocalPoint = () => {
    switch (focalPoint) {
      case "left-top":
        return `0,0,${cropWidthCalc},${cropHeightCalc}`
      case "right-top":
        return `${width - cropWidthCalc},0,${cropWidthCalc},${cropHeightCalc}`
      case "top":
        return `${
          (width - cropWidthCalc) / 2
        },0,${cropWidthCalc},${cropHeightCalc}`
      case "left-bottom":
        return `0,${height - cropHeightCalc},${cropWidthCalc},${cropHeightCalc}`
      case "right-bottom":
        return `${width - cropWidthCalc},${
          height - cropHeightCalc
        },${cropWidthCalc},${cropHeightCalc}`
      case "bottom":
        return `${(width - cropWidthCalc) / 2},${
          height - cropHeightCalc
        },${cropWidthCalc},${cropHeightCalc}`
      case "left":
        return `0,${
          (height - cropHeightCalc) / 2
        },${cropWidthCalc},${cropHeightCalc}`
      case "right":
        return `${width - cropWidthCalc},${
          (height - cropHeightCalc) / 2
        },${cropWidthCalc},${cropHeightCalc}`
      case "center":
      default:
        return `${(width - cropWidthCalc) / 2},${
          (height - cropHeightCalc) / 2
        },${cropWidthCalc},${cropHeightCalc}`
    }
  }

  const params = {
    w: cropWidth ? cropWidth : ``,
    h: cropWidth && cropRatio ? cropWidth * cropRatio : ``,
    rect: focalPoint ? rectFocalPoint() : ``,
    fit: !focalPoint && cropRatio ? "crop" : ``,
    q: 85,
    auto: "format",
  }
  const paramsLow = {
    w: 100,
    h: cropRatio ? 100 * cropRatio : ``,
    rect: focalPoint ? rectFocalPoint() : ``,
    fit: !focalPoint && cropRatio ? "crop" : ``,
    q: 85,
    auto: "format",
  }
  const params2x = {
    w: cropWidth ? cropWidth : ``,
    h: cropWidth && cropRatio ? cropWidth * cropRatio : ``,
    rect: focalPoint ? rectFocalPoint() : ``,
    fit: !focalPoint && cropRatio ? "crop" : ``,
    q: 50,
    dpr: 2,
    auto: "format",
  }
  const paramsString = stringify(params).replace(/%2C/g, ",")
  const paramsLowString = stringify(paramsLow).replace(/%2C/g, ",")
  const params2xString = stringify(params2x).replace(/%2C/g, ",")
  const { hydrated: isClient } = React.useContext(ThemeContext)
  const imageClassName = `${
    cover
      ? `${
          {
            "left-top": "object-left-top",
            top: "object-top",
            "right-top": "object-right-top",
            left: "object-left",
            center: "object-center",
            right: "object-right",
            "left-bottom": "object-left-bottom",
            bottom: "object-bottom",
            "right-bottom": "object-right-bottom",
          }[focalPoint || "center"]
        }`
      : ``
  }`
  const objectPosition = {
    "left-top": "left top",
    top: "top",
    "right-top": "right top",
    left: "left",
    center: "center",
    right: "right",
    "left-bottom": "left bottom",
    bottom: "bottom",
    "right-bottom": "right bottom",
  }[focalPoint || "center"]

  useEffect(() => {
    if (!isClient) {
      return
    }

    if (cover) {
      window.objectFitPolyfill()
    }
  }, [cover, isClient])

  return (
    <>
      <MediaBox
        imageWidth={width}
        imageHeight={height}
        className={className}
        cropRatio={cropRatio}
        cover={cover}
        {...mediaBoxProps}
      >
        <picture>
          <img
            {...(lazy
              ? {
                  src: `${url}?${paramsLowString}`,
                  "data-lowsrc": `${url}?${paramsLowString}`,
                  "data-src": `${url}?${paramsString}`,
                  "data-srcset": `${url}?${paramsString}, ${url}?${params2xString} 2x`,
                }
              : {
                  src: `${url}?${paramsString}`,
                  srcSet: `${url}?${paramsString}, ${url}?${params2xString} 2x`,
                })}
            className={
              lazy
                ? `image-optim__image ${
                    isClient ? "lazyload" : ""
                  } ${imageClassName}`
                : `image-optim__image ${imageClassName}`
            }
            alt={alt}
            data-object-fit={cover}
            data-object-position={cover && objectPosition}
            {...otherProps}
          />
        </picture>
      </MediaBox>
      {caption && !suppressCaption ? (
        <Caption
          className={`text-left`}
          border={region === "aside" ? "left" : undefined}
        >
          <RichText
            content={caption}
            linkedItems={captionLinkedItems}
            links={captionLinks}
            spacing="xs"
            theme={theme}
          />
        </Caption>
      ) : null}
    </>
  )
}

export default Picture
